import * as math from 'mathjs';
import { sanitizeExpression } from '../sanitizeExpression';

function findMatchingRightParen(value, leftIndex) {
  let depth = 0;
  for (let i = leftIndex; i < value.length; i += 1) {
    if (value[i] === '(') depth += 1;
    if (value[i] === ')') depth -= 1;
    if (depth === 0) return i;
  }
  return -1;
}

function isWrapped(value) {
  if (!value.startsWith('(') || !value.endsWith(')')) return false;
  return findMatchingRightParen(value, 0) === value.length - 1;
}

function unwrap(value) {
  let current = value.trim();
  while (isWrapped(current)) {
    current = current.slice(1, -1).trim();
  }
  return current;
}

function nextFactorEnd(value, slashIndex) {
  let i = slashIndex + 1;
  while (i < value.length && /\s/.test(value[i])) i += 1;
  if (value[i] === '(') {
    const end = findMatchingRightParen(value, i);
    return end >= 0 ? end + 1 : value.length;
  }
  while (i < value.length && !/[+\-*/]/.test(value[i])) i += 1;
  return i;
}

function extractDenominators(equation) {
  const sanitized = sanitizeExpression(equation);
  const denominators = [];
  for (let i = 0; i < sanitized.length; i += 1) {
    if (sanitized[i] !== '/') continue;
    const end = nextFactorEnd(sanitized, i);
    const denominator = unwrap(sanitized.slice(i + 1, end));
    if (denominator) denominators.push(denominator);
  }
  return denominators;
}

function getExcludedPoints(equation, xMin, xMax, step) {
  if (!equation.includes('/')) return [];
  const denominators = extractDenominators(equation);
  const excluded = [];
  const scanStep = Math.min(step / 4, 0.01);

  const pushRoot = (root) => {
    const rounded = Math.round(root * 10000) / 10000;
    if (rounded < xMin || rounded > xMax) return;
    if (!excluded.some((badX) => Math.abs(badX - rounded) < 0.01)) {
      excluded.push(rounded);
    }
  };

  const bisectRoot = (fn, left, right) => {
    let a = left;
    let b = right;
    let fa = fn.evaluate({ x: a });
    let fb = fn.evaluate({ x: b });
    for (let i = 0; i < 32; i += 1) {
      const mid = (a + b) / 2;
      const fm = fn.evaluate({ x: mid });
      if (!Number.isFinite(fm)) break;
      if (Math.abs(fm) < 1e-8) return mid;
      if (fa === 0) return a;
      if (fb === 0) return b;
      if (Math.sign(fa) !== Math.sign(fm)) {
        b = mid;
        fb = fm;
      } else {
        a = mid;
        fa = fm;
      }
    }
    return (a + b) / 2;
  };

  denominators.forEach((denominator) => {
    let fn;
    try { fn = math.parse(denominator).compile(); } catch { return; }

    let prevX = xMin;
    let prevVal = fn.evaluate({ x: prevX });
    if (Number.isFinite(prevVal) && Math.abs(prevVal) < 1e-3) {
      pushRoot(prevX);
    }

    for (let x = xMin + scanStep; x <= xMax; x += scanStep) {
      const val = fn.evaluate({ x });
      if (!Number.isFinite(val)) {
        prevX = x;
        prevVal = val;
        continue;
      }
      if (Math.abs(val) < 1e-3) {
        pushRoot(x);
      } else if (Number.isFinite(prevVal) && Math.sign(prevVal) !== Math.sign(val)) {
        pushRoot(bisectRoot(fn, prevX, x));
      }
      prevX = x;
      prevVal = val;
    }
  });

  return excluded.sort((a, b) => a - b);
}

function classifyExcludedPoint(fn, x, yMin, yMax) {
  const offsets = [1e-4, 1e-3, 1e-2, 5e-2];
  const ySpan = Math.max(1, yMax - yMin);
  const yMargin = Math.max(2, ySpan * 0.2);
  const hardYMin = yMin - yMargin;
  const hardYMax = yMax + yMargin;

  for (const offset of offsets) {
    try {
      const left = Number(fn.evaluate({ x: x - offset }));
      const right = Number(fn.evaluate({ x: x + offset }));
      if (!Number.isFinite(left) || !Number.isFinite(right)) continue;
      const y = (left + right) / 2;
      const closeEnough = Math.abs(left - right) <= Math.max(0.2, ySpan * 0.03);
      const inView = y >= hardYMin && y <= hardYMax;
      if (closeEnough && inView) return { type: 'hole', x, y };
    } catch {
      return { type: 'asymptote', x };
    }
  }

  return { type: 'asymptote', x };
}

export function generateRationalData(equation, xMin, xMax, yMin, yMax, step) {
  const safeEq = sanitizeExpression(equation);
  const excluded = getExcludedPoints(equation, xMin, xMax, step);
  const dataSegments = [];
  let fn;
  try { fn = math.compile(safeEq); } catch { return { segments: [], asymptotes: [], holes: [] }; }

  const classified = excluded.map((x) => classifyExcludedPoint(fn, x, yMin, yMax));
  const holes = classified.filter((item) => item.type === 'hole');
  const asymptotes = classified.filter((item) => item.type === 'asymptote').map((item) => item.x);

  let xSegment = [];
  let ySegment = [];
  let prevY = null;
  const yMargin = Math.max(2, (yMax - yMin) * 0.2);
  const hardYMin = yMin - yMargin;
  const hardYMax = yMax + yMargin;
  const jumpLimit = Math.max(4, (yMax - yMin) * 0.75);

  for (let xi = xMin; xi <= xMax; xi += step) {
    if (excluded.some((badX) => Math.abs(xi - badX) < step * 2)) {
      if (xSegment.length > 1) dataSegments.push({ x: [...xSegment], y: [...ySegment] });
      xSegment = [];
      ySegment = [];
      prevY = null;
      continue;
    }
    try {
      const val = fn.evaluate({ x: xi });
      if (!Number.isFinite(val) || val < hardYMin || val > hardYMax) {
        if (xSegment.length > 1) dataSegments.push({ x: [...xSegment], y: [...ySegment] });
        xSegment = [];
        ySegment = [];
        prevY = null;
        continue;
      }
      if (prevY !== null && Math.abs(val - prevY) > jumpLimit) {
        if (xSegment.length > 1) dataSegments.push({ x: [...xSegment], y: [...ySegment] });
        xSegment = [];
        ySegment = [];
        prevY = null;
        continue;
      }
      xSegment.push(xi);
      ySegment.push(val);
      prevY = val;
    } catch {
      if (xSegment.length > 1) dataSegments.push({ x: [...xSegment], y: [...ySegment] });
      xSegment = [];
      ySegment = [];
      prevY = null;
    }
  }
  if (xSegment.length > 1) dataSegments.push({ x: xSegment, y: ySegment });
  return { segments: dataSegments, asymptotes, holes };
}
